import { coverMediaKey, isInspectionPurged, isInspectionWorkable } from '../domain/inspection';
import { contextKindOf } from '../domain/inspectionContext';
import { deleteInspectionCascade } from '../persistence/v2Database';
import { aiJobsStore, removeAiJobsOfInspectionLocal } from './aiJobsStore';
import { aiStore, removeAiOfInspectionLocal } from './aiStore';
import { releaseInspectionCredit, type ReleaseResult } from './billingStore';
import { evidenceStore, removeEvidenceOfInspectionLocal } from './evidenceStore';
import { findingStore, removeFindingsOfInspectionLocal } from './findingStore';
import { inspectionStore, removeInspectionLocal } from './inspectionStore';
import { releaseMediaUrls } from './mediaUrls';
import { removeReportsOfInspectionLocal, reportStore } from './reportStore';

// What deleting this inspection would remove, so the confirmation can say it (NY F1 Round 5: after
// OD-12 an inspection may be nothing BUT its PDFs, and deleting it deletes the delivered report).
export interface DeleteInspectionImpact {
  photoCount: number;
  findingCount: number;
  pdfCount: number;
  // The working data is already gone: only the PDFs and the basic-information row are left.
  purged: boolean;
}

export function deleteInspectionImpact(inspectionId: string): DeleteInspectionImpact {
  const inspection = inspectionStore.get().inspections.find((i) => i.id === inspectionId) ?? null;
  return {
    photoCount: evidenceStore.get().evidence.filter((e) => e.inspectionId === inspectionId).length,
    findingCount: findingStore.get().findings.filter((f) => f.inspectionId === inspectionId).length,
    pdfCount: reportStore.get().reports.filter((r) => r.inspectionId === inspectionId).length,
    purged: inspection !== null && isInspectionPurged(inspection)
  };
}

// Deletes ONE inspection and everything that belongs to it, in one transaction, inside the
// yanwushi-v2 database only. Photos leave the app's store; the system album is never touched
// (there is no code path in this app that can delete from the album). AI runs and observations
// of the inspection go with it, and so do the generated PDFs — including the final report and the
// signed agreement of an inspection whose working data was already purged (OD-12).
//
// Owner 2026-10-20 (billingPolicy.ts V-1): an unfinished formal inspection deleted HERE gives back the free
// report / single purchase it took when the server agrees (no AI used, within the limit). A copy that was
// exported to another device gives nothing back — the inspection lives on over there.
export async function deleteInspectionAction(inspectionId: string): Promise<{ release: ReleaseResult }> {
  const inspection = inspectionStore.get().inspections.find((i) => i.id === inspectionId) ?? null;
  // Only a client engagement was ever paid for: a Demo or a test inspection has nothing to give back.
  const releasable = inspection !== null && isInspectionWorkable(inspection) && contextKindOf(inspection) === 'realClient';
  const state = inspectionStore.get();
  const areaIds = state.areas.filter((a) => a.inspectionId === inspectionId).map((a) => a.id);
  const itemIds = state.items.filter((i) => i.inspectionId === inspectionId).map((i) => i.id);
  const evidenceIds = evidenceStore.get().evidence.filter((e) => e.inspectionId === inspectionId).map((e) => e.id);
  const findingIds = findingStore.get().findings.filter((f) => f.inspectionId === inspectionId).map((f) => f.id);
  const reportIds = reportStore.get().reports.filter((r) => r.inspectionId === inspectionId).map((r) => r.id);
  const ai = aiStore.get();
  const aiRunIds = ai.runs.filter((r) => r.inspectionId === inspectionId).map((r) => r.id);
  const aiObservationIds = ai.observations.filter((s) => s.inspectionId === inspectionId).map((s) => s.id);
  const aiJobIds = aiJobsStore.get().jobs.filter((j) => j.inspectionId === inspectionId).map((j) => j.id);
  await deleteInspectionCascade(inspectionId, { areaIds, itemIds, evidenceIds, findingIds, reportIds, aiRunIds, aiObservationIds, aiJobIds });
  removeInspectionLocal(inspectionId);
  removeReportsOfInspectionLocal(inspectionId);
  removeEvidenceOfInspectionLocal(inspectionId);
  removeFindingsOfInspectionLocal(inspectionId);
  removeAiOfInspectionLocal(inspectionId);
  removeAiJobsOfInspectionLocal(inspectionId);
  releaseMediaUrls([...evidenceIds, coverMediaKey(inspectionId)]);
  // The deletion stands whatever the server says (or whether it can be reached at all).
  const release = releasable ? await releaseInspectionCredit(inspectionId).catch((): ReleaseResult => ({ kind: 'queued' })) : ({ kind: 'none' } as const);
  return { release };
}
